/**
 * Locate a JSON-pointer (RFC 6901) inside the EXACT text the author is editing, so a diagnostic's
 * `path` can focus/select the offending node in the textarea. This is a tiny offset-tracking JSON
 * scanner over the buffer as-typed (never a re-serialisation — offsets must match what the author
 * sees). Returns null when the text does not parse or the pointer resolves to nothing.
 */

export interface Span {
  start: number;
  end: number;
}

/** 1-based line/column of a character offset, for display next to a diagnostic. */
export function lineColOf(text: string, offset: number): { line: number; col: number } {
  let line = 1;
  let col = 1;
  const end = Math.min(offset, text.length);
  for (let i = 0; i < end; i++) {
    if (text[i] === '\n') {
      line++;
      col = 1;
    } else {
      col++;
    }
  }
  return { line, col };
}

/** RFC 6901 pointer → decoded segments ('' → [], '/a/~1b/0' → ['a','/b','0']). */
function segmentsOf(pointer: string): string[] {
  if (pointer === '' || pointer === '/') return pointer === '/' ? [''] : [];
  return pointer
    .slice(1)
    .split('/')
    .map((s) => s.replace(/~1/g, '/').replace(/~0/g, '~'));
}

class Scanner {
  private i = 0;

  constructor(private readonly text: string) {}

  /** Span of the value the pointer addresses, or null (bad JSON / unresolved pointer). */
  locate(pointer: string): Span | null {
    try {
      const span = this.value(segmentsOf(pointer), 0);
      this.ws();
      return span;
    } catch {
      return null;
    }
  }

  private ws(): void {
    while (this.i < this.text.length && /[ \t\n\r]/.test(this.text[this.i])) this.i++;
  }

  private fail(): never {
    throw new Error('bad json at ' + this.i);
  }

  /**
   * Parse one value; when `target` is exhausted (depth === target.length) the value's own span is
   * the answer. Descends only where the path matches — siblings are skimmed with the same code.
   */
  private value(target: string[], depth: number): Span | null {
    this.ws();
    const start = this.i;
    const c = this.text[this.i];
    if (c === undefined) this.fail();
    let found: Span | null = null;
    if (c === '{') {
      found = this.object(target, depth);
    } else if (c === '[') {
      found = this.array(target, depth);
    } else if (c === '"') {
      this.string();
    } else {
      this.literal();
    }
    if (found) return found;
    return depth === target.length ? { start, end: this.i } : null;
  }

  private object(target: string[], depth: number): Span | null {
    this.i++; // {
    let found: Span | null = null;
    this.ws();
    if (this.text[this.i] === '}') {
      this.i++;
      return found;
    }
    for (;;) {
      this.ws();
      if (this.text[this.i] !== '"') this.fail();
      const key = this.string();
      this.ws();
      if (this.text[this.i] !== ':') this.fail();
      this.i++;
      const descend = depth < target.length && target[depth] === key;
      const span = this.value(descend ? target : [], descend ? depth + 1 : 1);
      if (descend && span) found = found ?? span;
      this.ws();
      if (this.text[this.i] === ',') {
        this.i++;
        continue;
      }
      if (this.text[this.i] === '}') {
        this.i++;
        return found;
      }
      this.fail();
    }
  }

  private array(target: string[], depth: number): Span | null {
    this.i++; // [
    let found: Span | null = null;
    this.ws();
    if (this.text[this.i] === ']') {
      this.i++;
      return found;
    }
    for (let index = 0; ; index++) {
      const descend = depth < target.length && target[depth] === String(index);
      const span = this.value(descend ? target : [], descend ? depth + 1 : 1);
      if (descend && span) found = found ?? span;
      this.ws();
      if (this.text[this.i] === ',') {
        this.i++;
        continue;
      }
      if (this.text[this.i] === ']') {
        this.i++;
        return found;
      }
      this.fail();
    }
  }

  /** Consume a JSON string, returning its decoded value (for key matching). */
  private string(): string {
    this.i++; // opening quote
    let out = '';
    for (;;) {
      const c = this.text[this.i];
      if (c === undefined) this.fail();
      if (c === '"') {
        this.i++;
        return out;
      }
      if (c === '\\') {
        const e = this.text[this.i + 1];
        if (e === undefined) this.fail();
        if (e === 'u') {
          const hex = this.text.slice(this.i + 2, this.i + 6);
          if (!/^[0-9a-fA-F]{4}$/.test(hex)) this.fail();
          out += String.fromCharCode(parseInt(hex, 16));
          this.i += 6;
        } else {
          const map: Record<string, string> = {
            '"': '"',
            '\\': '\\',
            '/': '/',
            b: '\b',
            f: '\f',
            n: '\n',
            r: '\r',
            t: '\t',
          };
          if (!(e in map)) this.fail();
          out += map[e];
          this.i += 2;
        }
      } else {
        out += c;
        this.i++;
      }
    }
  }

  private literal(): void {
    const m = /^(?:true|false|null|-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)/.exec(
      this.text.slice(this.i),
    );
    if (!m) this.fail();
    this.i += m[0].length;
  }
}

/**
 * Span of the node a JSON pointer addresses within `text`. A pointer to a missing node walks up:
 * `/screens/2/kind` with no `kind` key falls back to `/screens/2`, then `/screens` — a diagnostic
 * about an ABSENT field still focuses the object that should carry it.
 */
export function locatePointer(text: string, pointer: string): Span | null {
  const segments = segmentsOf(pointer);
  for (let take = segments.length; take >= 0; take--) {
    const partial =
      take === 0 ? '' : '/' + segments.slice(0, take).map(encodeSegment).join('/');
    const span = new Scanner(text).locate(partial);
    if (span) return span;
  }
  return null;
}

function encodeSegment(s: string): string {
  return s.replace(/~/g, '~0').replace(/\//g, '~1');
}
